import React, { useState } from 'react';
import { LivePage, FilterBar, DataTable, Age, Code, assertExecOk, clustersAPI, useNamespaces, NamespaceSelect } from './LivePage';
import { useCluster } from '../context/ClusterContext';
import { useAuth } from '../context/AuthContext';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

const lbl = (text) => <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:4 }}>{text}</label>;

const SECRET_TYPES = [
  { value:'Opaque', label:'Opaque (generic key-value)' },
  { value:'kubernetes.io/dockerconfigjson', label:'Docker Registry' },
  { value:'kubernetes.io/tls', label:'TLS Certificate' },
  { value:'kubernetes.io/basic-auth', label:'Basic Auth' },
  { value:'kubernetes.io/ssh-auth', label:'SSH Auth' },
];

// Docker Registry and TLS map to a dedicated `kubectl create secret` subcommand with its
// OWN fixed flag names (--docker-server, --cert, etc. — see handleCreate) — unlike Opaque/
// basic-auth/ssh-auth, which just pass through whatever keys the user types as
// --from-literal. Locking these two types' key names in the UI (required, not editable,
// not removable) means the keys always match what the actual kubectl flags need, so the
// command built in handleCreate can rely on finding them by exact name.
const REQUIRED_KEYS = {
  'kubernetes.io/dockerconfigjson': ['docker-server', 'docker-username', 'docker-password', 'docker-email'],
  'kubernetes.io/tls': ['tls.crt', 'tls.key'],
};

function CreateSecretModal({ clusterId, initialNamespace = 'default', namespaces = [], onClose, onCreated }) {
  const [form, setForm] = useState({ name:'', namespace: initialNamespace, type:'Opaque' });
  const [pairs, setPairs] = useState([{ key:'', value:'' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const updatePair = (i, field, val) => setPairs(p => p.map((x,idx) => idx===i ? {...x,[field]:val} : x));
  const addPair    = () => setPairs(p => [...p, {key:'', value:''}]);
  const removePair = (i) => setPairs(p => p.filter((_,idx) => idx!==i));
  const requiredKeys = REQUIRED_KEYS[form.type] || null;

  const changeType = (type) => {
    setF('type', type);
    const req = REQUIRED_KEYS[type];
    setPairs(req ? req.map(key => ({ key, value: '' })) : [{ key:'', value:'' }]);
  };

  const handleCreate = async () => {
    if (!form.name) { setError('Name is required'); return; }
    const validPairs = pairs.filter(p => p.key.trim());
    if (validPairs.length === 0) { setError('At least one key-value pair required'); return; }
    setSaving(true); setError('');
    try {
      // `kubectl create secret` only has THREE real subcommands — generic, docker-registry,
      // tls — each with its OWN flag set. Using form.type (the k8s `type:` field value,
      // e.g. "kubernetes.io/dockerconfigjson") as the subcommand name is invalid for every
      // non-Opaque type, and docker-registry/tls don't accept --from-literal at all (only
      // generic does) — confirmed live: this previously failed with "unknown flag:
      // --from-literal" for Docker Registry. Basic-auth/ssh-auth have no dedicated
      // subcommand — k8s doesn't enforce their key names, so `generic` + --from-literal
      // already works correctly for those two.
      let secretCmd;
      if (form.type === 'kubernetes.io/dockerconfigjson') {
        const byKey = Object.fromEntries(validPairs.map(p => [p.key, p.value]));
        const flags = [
          byKey['docker-server']   && `--docker-server=${byKey['docker-server']}`,
          byKey['docker-username'] && `--docker-username=${byKey['docker-username']}`,
          byKey['docker-password'] && `--docker-password=${byKey['docker-password']}`,
          byKey['docker-email']    && `--docker-email=${byKey['docker-email']}`,
        ].filter(Boolean).join(' ');
        secretCmd = `kubectl create secret docker-registry ${form.name} -n ${form.namespace} ${flags}`;
      } else if (form.type === 'kubernetes.io/tls') {
        // kubectl create secret tls --cert/--key expect FILE PATHS, not pasted PEM text —
        // there's no way to turn this form's text fields into a path through this exec-based
        // flow (no file upload here). Needs a different approach (e.g. a YAML apply with
        // base64-encoded data instead of this subcommand) — not yet implemented, so fail
        // clearly rather than send kubectl a path argument that's actually certificate text.
        setError('TLS secret creation via this form is not yet supported — paste-based input cannot be passed to kubectl create secret tls (it expects file paths). Use kubectl directly for now.');
        setSaving(false);
        return;
      } else {
        const fromLiterals = validPairs.map(p => `--from-literal=${p.key}=${p.value}`).join(' ');
        secretCmd = `kubectl create secret generic ${form.name} -n ${form.namespace} ${fromLiterals}`;
      }
      const cmd = `kubectl create namespace ${form.namespace} --dry-run=client -o yaml | kubectl apply -f - 2>/dev/null; ${secretCmd} --dry-run=client -o yaml | kubectl apply -f -`;
      const r = await clustersAPI.exec(clusterId, cmd, { section: 'configuration', action: 'create' });
      const out = r.data?.output || r.data?.stdout || '';
      const err = r.data?.stderr || '';
      const combined = `${out}\n${err}`.trim();
      const ok = /(created|configured|unchanged)/.test(combined) && !/error|denied|forbidden/i.test(err);
      if (ok) { onCreated(); onClose(); }
      else setError(combined || 'Create failed (no output from cluster — is the cluster agent connected?)');
    } catch (e) { setError(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="card" style={{ width:540, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>Create Secret</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon" style={{ fontSize:20 }}>✕</button>
        </div>
        <div className="banner warning" style={{ marginBottom:16 }}>
          ⚠ Secret values are stored in Kubernetes — avoid committing them to version control.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>{lbl('Name *')}<input placeholder="my-secret" value={form.name} onChange={e=>setF('name',e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))} className="input" /></div>
            <div>{lbl('Namespace')}<NamespaceSelect namespaces={namespaces} ns={form.namespace} onChange={v=>setF('namespace',v)} /></div>
          </div>
          <div>
            {lbl('Type')}
            <select value={form.type} onChange={e=>changeType(e.target.value)} className="select">
              {SECRET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            {lbl('Key-Value Data')}
            <div style={{ border:'1px solid var(--border)', borderRadius:7, overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', background:'var(--surface-2)', padding:'6px 10px', fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                <span>Key</span><span>Value (plaintext — stored as base64)</span><span></span>
              </div>
              {pairs.map((pair, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', gap:4, padding:'6px 10px', borderTop:'1px solid var(--border)', alignItems:'center' }}>
                  <input placeholder="SECRET_KEY" value={pair.key} readOnly={!!requiredKeys}
                    onChange={e=>updatePair(i,'key',e.target.value)}
                    className="input" style={{ fontFamily:'monospace', fontSize:12, background: requiredKeys ? 'var(--surface-2)' : undefined }} />
                  <input type="password" placeholder={requiredKeys && pair.key === 'docker-email' ? 'secret value (optional)' : 'secret value'}
                    value={pair.value} onChange={e=>updatePair(i,'value',e.target.value)}
                    className="input" style={{ fontSize:12 }} />
                  {!requiredKeys && (
                    <button onClick={()=>removePair(i)} disabled={pairs.length===1}
                      className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--color-danger-500)', fontSize:16 }}>×</button>
                  )}
                </div>
              ))}
              {!requiredKeys && (
                <div style={{ padding:'8px 10px', borderTop:'1px solid var(--border)' }}>
                  <button onClick={addPair} className="btn btn-link">+ Add key</button>
                </div>
              )}
            </div>
          </div>
          {error && <div className="banner danger">{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary" style={{ flex:1 }}>
              {saving ? '⟳ Creating...' : '+ Create Secret'}
            </button>
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Secrets() {
  const { hasSectionAction } = useAuth();
  const { selectedCluster } = useCluster();
  const canCreate = hasSectionAction('configuration', 'create');
  const canDelete = hasSectionAction('configuration', 'delete');
  const { namespaces, ns, setNs } = useNamespaces(selectedCluster?.id);
  const [nameFilter, setNameFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  return (
    <LivePage title="Secrets" deps={[ns]} waitFor={ns} fetchFn={id => clustersAPI.getWorkloadPaged('secrets', id, { pageSize: 500, namespace: ns || undefined })}>
      {(data, refresh, clusterId) => {
        // API returns a raw K8s List (items mixing ConfigMap+Secret); normalize
        // Secret items into the flat shape the table expects.
        const _raw = data.secrets || data.items || [];
        const items = _raw
          .filter(it => (it.kind || 'Secret') === 'Secret')
          .map(it => it.metadata ? {
            name: it.metadata.name,
            namespace: it.metadata.namespace,
            type: it.type || 'Opaque',
            created_at: it.metadata.creationTimestamp,
          } : it);
        const filtered = items.filter(s =>
          s.name.toLowerCase().includes(nameFilter.toLowerCase())
        );

        const handleDelete = async (name, namespace) => {
          if (!window.confirm(`Delete secret "${name}" in "${namespace}"?\n\nThis will permanently delete the secret.`)) return;
          try {
            const r = await clustersAPI.exec(clusterId, `kubectl delete secret ${name} -n ${namespace} --ignore-not-found=true`, { section: 'configuration', action: 'delete' });
            assertExecOk(r, 'Delete');   // kubectl failures come back HTTP 200 on stderr
            refresh();
          } catch (e) { alert(`Delete failed: ${e.response?.data?.detail || e.message}`); }
        };

        return <>
          <div className="no-cluster-msg" style={{ marginBottom:16, background:'var(--color-warning-50)', borderColor:'var(--color-warning-500)', color:'var(--color-warning-700)' }}>
            Secret values are never shown — names and types only.
          </div>
          <FilterBar count={filtered.length} total={items.length} label="secrets">
            <NamespaceSelect namespaces={namespaces} ns={ns} onChange={setNs} />
            <input placeholder="Filter by name..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} />
            {canCreate && (
            <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }}>
              + Create Secret
            </button>
            )}
          </FilterBar>
          <DataTable cols={['Name', 'Namespace', 'Type', 'Age', '']}
            rows={filtered.map(s => (
              <tr key={`${s.namespace}/${s.name}`}>
                <td><Code>{s.name}</Code></td>
                <td style={{ color:'var(--ink-2)', fontSize:13 }}>{s.namespace}</td>
                <td style={{ fontSize:12, color:'var(--muted)' }}>{s.type}</td>
                <td><Age ts={s.created_at} /></td>
                <td>
                  {canDelete && (
                  <ActionsMenu>
                    <button onClick={() => handleDelete(s.name, s.namespace)} style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}>
                      Delete
                    </button>
                  </ActionsMenu>
                  )}
                </td>
              </tr>
            ))} />
          {showCreate && (
            <CreateSecretModal
              clusterId={clusterId}
              initialNamespace={ns || 'default'}
              namespaces={namespaces}
              onClose={() => setShowCreate(false)}
              onCreated={() => { setShowCreate(false); refresh(); }}
            />
          )}
        </>;
      }}
    </LivePage>
  );
}
